import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Camera,
  CheckSquare,
  ArrowLeft,
  ArrowRight,
  Eye,
  Filter,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ApprovalFlowProps {
  currentPage?: 'gallery-management' | 'approval-queue' | 'gallery-detail';
  galleryId?: string;
  pendingCount?: number;
  filters?: {
    type?: 'galleries' | 'images' | 'all';
    region?: string;
    status?: string;
  };
  className?: string;
}

export function ApprovalFlow({
  currentPage,
  pendingCount = 0,
  filters,
  className,
}: ApprovalFlowProps) {
  // Build filtered approval queue URL
  const buildApprovalUrl = (additionalFilters?: Record<string, string>) => {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.region) params.set('region', filters.region);
    if (filters?.status) params.set('status', filters.status);

    // Add additional filters
    if (additionalFilters) {
      Object.entries(additionalFilters).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    const queryString = params.toString();
    return `/media/approval${queryString ? `?${queryString}` : ''}`;
  };

  const getBackLink = () => {
    if (currentPage === 'approval-queue') {
      return {
        href: '/media/galleries',
        label: 'Back to Gallery Management',
        icon: Camera,
      };
    }
    if (currentPage === 'gallery-detail') {
      return {
        href: '/media/galleries',
        label: 'Back to Gallery Management',
        icon: Camera,
      };
    }
    return null;
  };

  const getForwardLink = () => {
    if (currentPage === 'gallery-management') {
      return {
        href: buildApprovalUrl(),
        label: 'Review Approvals',
        icon: CheckSquare,
        badge: pendingCount > 0 ? pendingCount : undefined,
      };
    }
    if (currentPage === 'gallery-detail') {
      return {
        href: buildApprovalUrl({ type: 'galleries' }),
        label: 'Review Galleries',
        icon: CheckSquare,
      };
    }
    return null;
  };

  const backLink = getBackLink();
  const forwardLink = getForwardLink();

  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      {/* Back Navigation */}
      <div className='flex items-center gap-2'>
        {backLink && (
          <Button variant='outline' size='sm' asChild>
            <Link to={backLink.href}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              <backLink.icon className='h-4 w-4 mr-2' />
              {backLink.label}
            </Link>
          </Button>
        )}
      </div>

      {/* Current Page Indicator */}
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        {currentPage === 'gallery-management' && (
          <>
            <Camera className='h-4 w-4' />
            <span>Gallery Management</span>
          </>
        )}
        {currentPage === 'approval-queue' && (
          <>
            <CheckSquare className='h-4 w-4' />
            <span>Approval Queue</span>
            {pendingCount > 0 && (
              <Badge variant='secondary'>{pendingCount} pending</Badge>
            )}
          </>
        )}
        {currentPage === 'gallery-detail' && (
          <>
            <Eye className='h-4 w-4' />
            <span>Gallery Details</span>
          </>
        )}
      </div>

      {/* Forward Navigation */}
      <div className='flex items-center gap-2'>
        {/* Quick Filters for Approval Queue */}
        {currentPage === 'gallery-management' && (
          <div className='flex items-center gap-1'>
            <Button variant='ghost' size='sm' asChild>
              <Link to={buildApprovalUrl({ type: 'galleries' })}>
                <Filter className='h-4 w-4 mr-1' />
                Galleries
              </Link>
            </Button>
            <Button variant='ghost' size='sm' asChild>
              <Link to={buildApprovalUrl({ type: 'images' })}>
                <Filter className='h-4 w-4 mr-1' />
                Images
              </Link>
            </Button>
          </div>
        )}

        {/* Reset Filters */}
        {currentPage === 'approval-queue' &&
          (filters?.type || filters?.region) && (
            <Button variant='ghost' size='sm' asChild>
              <Link to='/media/approval'>
                <RotateCcw className='h-4 w-4 mr-2' />
                Clear Filters
              </Link>
            </Button>
          )}

        {/* Main Forward Link */}
        {forwardLink && (
          <Button
            size='sm'
            variant={
              currentPage === 'gallery-management' ? 'outline' : 'default'
            }
            asChild>
            <Link to={forwardLink.href}>
              <forwardLink.icon className='h-4 w-4 mr-2' />
              {forwardLink.label}
              {forwardLink.badge && (
                <Badge variant='secondary' className='ml-2'>
                  {forwardLink.badge}
                </Badge>
              )}
              <ArrowRight className='h-4 w-4 ml-2' />
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}

export default ApprovalFlow;
